import type { ChipOption } from '#components/elements/ToggleChips.model';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useLanguage } from '#composables/useLanguage';
import { useAnalytics } from '#domain/core/analytics/composables/useAnalytics';
import { getDataTableAvailableDimensionsList } from '#domain/performance/kpi/kpi.query';
import { useDataTableStore } from '#stores/dataTable.store';
import {
  DATE_GRANULARITY_DIMENSIONS,
  DEFAULT_SELECTED_DIMENSIONS_DATA_TABLE,
  DIMENSION_DEPENDENCY_RULES_PER_DATATABLE_VIEW,
  DIMENSIONS_ENABLED_BY_FILTERS,
  isDataTableDimensionsType,
  sortFromCompareArray,
  type DataTableDimensionsType,
  type DataTableViewType,
  type DatePeriod
} from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, ref, unref, watch, type Ref } from 'vue';

interface UseDataTableDimensionParams {
  dataTableView: Ref<DataTableViewType>;
}

export function useDataTableDimensions({ dataTableView }: UseDataTableDimensionParams) {
  const { t } = useLanguage();
  const dataTableStore = useDataTableStore();
  const { trackEvent } = useAnalytics();

  const {
    taxonomy: { taxonomy },
    period: { selectedPeriod },
    filters: { selectedFiltersTree }
  } = useKpisPerimeter();
  const { data: availableDimensions, isFetching: isFetchingDimensions } = useQuery({
    queryKey: ['availableDimensions', taxonomy, dataTableView, selectedPeriod],
    queryFn: async () =>
      await getDataTableAvailableDimensionsList({
        taxonomy: unref(taxonomy),
        dataTableView: unref(dataTableView),
        period: unref(selectedPeriod).period
      })
  });

  const selectedDimensions = ref<DataTableDimensionsType[]>(getDefaultSelectedDimensions());
  const disabledDimensions = ref<DataTableDimensionsType[]>([]);

  watch(taxonomy, () => {
    selectedDimensions.value = getDefaultSelectedDimensions();
  });

  watch(
    () => selectedPeriod.value.period,
    (newPeriod) => {
      const currentOrder = dataTableStore.getDimensionsOrder(taxonomy.value, dataTableView.value);
      if (currentOrder) {
        const mappedOrder = [...currentOrder];
        cleanStaleDateGranularityDimensions(newPeriod, mappedOrder);
        const validatedOrder = mappedOrder.filter(isDataTableDimensionsType);
        dataTableStore.updateDimensionsOrder(taxonomy.value, dataTableView.value, validatedOrder);
      }
    }
  );

  watch(
    availableDimensions,
    (availableDimensions) => {
      const savedSelectedDimensions = dataTableStore.getSelectedDimensions(taxonomy.value, dataTableView.value);
      if (availableDimensions?.length) {
        if (savedSelectedDimensions) {
          const dimensionsToFilter = [...savedSelectedDimensions];
          cleanStaleDateGranularityDimensions(selectedPeriod.value.period, dimensionsToFilter);

          const savedSelectedDimensionsFiltered = dimensionsToFilter.filter((dimension) =>
            availableDimensions.find((availableDimension) => availableDimension.name === dimension)
          );

          updateSelectedDimensions(
            savedSelectedDimensionsFiltered.length ? savedSelectedDimensionsFiltered : getDefaultSelectedDimensions()
          );
        } else if (selectedDimensions.value.length === 0) {
          updateSelectedDimensions(getDefaultSelectedDimensions());
        } else if (
          selectedDimensions.value.some((dimension) => !availableDimensions.find((item) => item.name === dimension))
        ) {
          const dimensionsToFilter = [...selectedDimensions.value];
          cleanStaleDateGranularityDimensions(selectedPeriod.value.period, dimensionsToFilter);

          const availableSelectedDimensions = dimensionsToFilter.filter((dimension) =>
            availableDimensions.find((item) => item.name === dimension)
          );

          updateSelectedDimensions(
            availableSelectedDimensions.length ? availableSelectedDimensions : getDefaultSelectedDimensions()
          );
        }
      }
    },
    {
      immediate: true
    }
  );

  function getDefaultSelectedDimensions(): DataTableDimensionsType[] {
    if (availableDimensions.value?.length) {
      let defaultSelectedDimensions = DEFAULT_SELECTED_DIMENSIONS_DATA_TABLE[dataTableView.value];

      // Remove unavailable dimensions in default selected dimensions
      if (
        defaultSelectedDimensions?.some(
          (defaultDimension) =>
            !availableDimensions.value.find((availableDimension) => availableDimension.name === defaultDimension)
        )
      ) {
        defaultSelectedDimensions = defaultSelectedDimensions.filter((defaultDimension) =>
          availableDimensions.value.find((availableDimension) => availableDimension.name === defaultDimension)
        );
      }
      if (defaultSelectedDimensions?.length) {
        return defaultSelectedDimensions;
      }
    }

    const firstDimension = availableDimensions.value?.[0];
    return availableDimensions.value?.length && firstDimension ? [firstDimension.name] : [];
  }

  function updateSelectedDimensions(dimensions: string[]) {
    if (!dimensions.every(isDataTableDimensionsType)) {
      throw new Error(`Invalid dimensions "${dimensions.join(', ')}"`);
    }

    // Map and preserve date granularity dimension on period change
    cleanStaleDateGranularityDimensions(selectedPeriod.value.period, dimensions);

    const dimensionsOrder = dataTableStore.getDimensionsOrder(taxonomy.value, dataTableView.value) ?? [];

    let filteredDimensions = dimensions.filter((dimension) => !disabledDimensions.value.includes(dimension));

    if (dimensionsOrder.length) {
      filteredDimensions = filteredDimensions.sort(sortFromCompareArray(dimensionsOrder));
    }

    selectedDimensions.value = filteredDimensions;
    dataTableStore.updateSelectedDimensions(taxonomy.value, dataTableView.value, dimensions);

    trackEvent({
      category: 'datatable',
      event: 'change_tab',
      action: 'changed',
      customFields: {
        tabs_names: dimensions
          .map((dimension) => availableDimensions.value?.find((item) => item.name === dimension)?.label)
          .filter(Boolean)
          .map((dimensionLabel) => t(dimensionLabel))
      }
    });
  }

  function resetSelectedDimensions() {
    dataTableStore.resetSelectedDimensions(taxonomy.value, dataTableView.value);
    updateSelectedDimensions(getDefaultSelectedDimensions());
  }

  function cleanStaleDateGranularityDimensions(selectedPeriod: DatePeriod | 'nielsen', dimensions: string[]) {
    if (selectedPeriod === 'nielsen') {
      return dimensions;
    }

    const correctDimension = {
      daily: 'date_key_evolution_date',
      weekly: 'date_key_evolution_week',
      monthly: 'date_key_evolution_month'
    }[selectedPeriod];

    const existingIndex = dimensions.findIndex((dim) => DATE_GRANULARITY_DIMENSIONS.includes(dim));

    if (existingIndex !== -1) {
      dimensions[existingIndex] = correctDimension;
    }

    const disallowed = DATE_GRANULARITY_DIMENSIONS.filter((dim) => dim !== correctDimension);

    const filtered = dimensions.filter((dim) => !disallowed.includes(dim));
    dimensions.length = 0;
    dimensions.push(...filtered);
  }

  const areDimensionsFetched = computed(() => (selectedDimensions.value?.length ?? 0) > 0);

  const dimensionChips = computed<ChipOption[]>(() => {
    disabledDimensions.value.splice(0, disabledDimensions.value.length);

    const dimensionsOrder = dataTableStore.getDimensionsOrder(taxonomy.value, dataTableView.value) ?? [];

    const chips =
      availableDimensions.value?.map((dimension) => {
        // Update disabled dimensions based on current selected dimensions, applying inclusion/exclusion rules defined in DIMENSION_DEPENDENCY_RULES_PER_DATATABLE_VIEW
        updateDimensionsStateBySelectedDimensions();

        // Update disabled state of the current dimension based on selected filters, according to rules defined in DIMENSIONS_ENABLED_BY_FILTERS
        updateDimensionStateBySelectedFilters(dimension.name);

        const isDimensionChecked = selectedDimensions.value.includes(dimension.name);

        const isDimensionDisabled = disabledDimensions.value.includes(dimension.name);

        const showTootip =
          DIMENSIONS_ENABLED_BY_FILTERS[dimension.name]?.tooltip &&
          (isDimensionDisabled || DIMENSIONS_ENABLED_BY_FILTERS[dimension.name]?.showTooltipWhenEnabled);

        return {
          value: dimension.name,
          label: t(dimension.label),
          checked: isDimensionChecked,
          disabled: isDimensionDisabled,
          tooltip: showTootip ? t(DIMENSIONS_ENABLED_BY_FILTERS[dimension.name]?.tooltip ?? '') : undefined
        };
      }) ?? [];

    if (dimensionsOrder.length) {
      return chips.sort((a, b) => sortFromCompareArray(dimensionsOrder)(a.value, b.value));
    }

    return chips;
  });

  function updateDimensionsStateBySelectedDimensions() {
    const dataTableViewRules = DIMENSION_DEPENDENCY_RULES_PER_DATATABLE_VIEW[dataTableView.value];

    if (!dataTableViewRules) {
      return;
    }

    for (const dimension of selectedDimensions.value) {
      const dimensionRule = dataTableViewRules[dimension];

      if (dimensionRule?.excludes) {
        dimensionRule.excludes.forEach((item) => {
          if (!disabledDimensions.value.includes(item)) {
            disabledDimensions.value.push(item);
          }
        });
      }
    }
  }

  function updateDimensionStateBySelectedFilters(dimension: DataTableDimensionsType) {
    if (!DIMENSIONS_ENABLED_BY_FILTERS[dimension]) {
      return;
    }

    const isDimensionDisabled = DIMENSIONS_ENABLED_BY_FILTERS[dimension]!.requiredFilters.every(
      (filter) => Object.keys(selectedFiltersTree.value[filter]).length === 0
    );

    if (isDimensionDisabled) {
      disabledDimensions.value.push(dimension);
      if (selectedDimensions.value.includes(dimension)) {
        selectedDimensions.value.splice(selectedDimensions.value.indexOf(dimension), 1);
      }
    }
  }

  return {
    areDimensionsFetched,
    dimensionChips,
    selectedDimensions,
    availableDimensions,
    disabledDimensions,
    updateSelectedDimensions,
    resetSelectedDimensions,
    isFetchingDimensions
  };
}
